import {
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import React, { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Bot,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Coins,
  Edit3,
  Globe,
  Info,
  Lightbulb,
  Loader2,
  Mail,
  MessageSquare,
  Minus,
  Plus,
  RefreshCw,
  Save,
  Send,
  Sparkles,
  Star,
  Target,
  ThumbsDown,
  ThumbsUp,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";
import { Button, Card, PageLayout } from "../components";
import { CampaignStrategySkeleton } from "../components/skeletons";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError, showToastSuccess } from "../shared/alertService";
import adsStrategyService, {
  BudgetAllocation,
  OrganicStrategyData,
  RecommendedCampaign,
  RefinementData,
  StrategyData,
} from "../services/adsStrategyService";
import { cn } from "../utils/cn";

// ─── Formatador de Moeda ──────────────────────────────────────────────────────
const fmtCurrency = (v: number) =>
  v.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

// ─── Configuração dos Agentes Virtuais ─────────────────────────────────────────
const AGENT_CONFIGS = {
  strategist: {
    id: "strategist",
    apiId: "sarah" as const,
    name: "Sarah (CMO)",
    role: "Estratégia & Visão Geral",
    badge: "Estrategista Líder",
    icon: Sparkles,
    color: "bg-purple-600 text-white",
    lightBg: "bg-purple-50/40",
    border: "border-purple-200",
    textColor: "text-purple-700",
    glow: "shadow-purple-500/10",
  },
  traffic: {
    id: "traffic",
    apiId: "max" as const,
    name: "Max (Tráfego)",
    role: "Especialista em Canais & Ads",
    badge: "Tráfego Pago",
    icon: Target,
    color: "bg-blue-600 text-white",
    lightBg: "bg-blue-50/40",
    border: "border-blue-200",
    textColor: "text-blue-700",
    glow: "shadow-blue-500/10",
  },
  data: {
    id: "data",
    apiId: "alex" as const,
    name: "Alex (Analytics)",
    role: "Analista de Dados & Otimização",
    badge: "Alocação & Finanças",
    icon: BarChart3,
    color: "bg-emerald-600 text-white",
    lightBg: "bg-emerald-50/40",
    border: "border-emerald-200",
    textColor: "text-emerald-700",
    glow: "shadow-emerald-500/10",
  },
  social: {
    id: "social",
    apiId: "mia" as const,
    name: "Mia (Social Media)",
    role: "Conteúdo & Engajamento",
    badge: "Orgânico & Redes",
    icon: Users,
    color: "bg-pink-600 text-white",
    lightBg: "bg-pink-50/40",
    border: "border-pink-200",
    textColor: "text-pink-700",
    glow: "shadow-pink-500/10",
  },
};

// ─── Header de Seção Padronizado ──────────────────────────────────────────────
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  iconBgClass?: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, iconBgClass = "bg-purple-600", action }) => (
  <div className="flex items-start gap-2.5 border-b pb-2 border-purple-100 mb-2.5 shrink-0">
    <div
      className={cn(
        "w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-white shadow-2xs",
        iconBgClass,
      )}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0 h-8 flex flex-col justify-between">
      <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-tight leading-none truncate">
        {title}
      </h3>
      <span className="text-[11px] text-gray-400 font-medium leading-none truncate">
        {subtitle}
      </span>
    </div>
    {action}
  </div>
);

// ─── Componente de Card de Campanha Recomendada ───────────────────────────────
const CampaignCard: React.FC<{ camp: RecommendedCampaign; idx: number }> = ({
  camp,
}) => {
  const { t } = useTranslation();
  const isCritical = camp.priority >= 4;
  const isLinkedIn = (camp.channel || "").toLowerCase().includes("linkedin");
  const ChannelIcon = isLinkedIn ? Users : Mail;

  return (
    <div className="w-full rounded-xl border border-blue-100 bg-white p-3 shadow-2xs hover:shadow-xs transition-all duration-200 flex flex-col justify-between gap-2.5">
      <div className="space-y-2">
        {/* Header do Card */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-100 text-blue-600 shrink-0">
              <ChannelIcon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <span className="text-[9px] uppercase tracking-widest text-blue-600 font-black block leading-none truncate">
                {camp.channel}
              </span>
              <h4 className="text-xs font-bold text-gray-900 leading-tight truncate mt-0.5">
                {camp.campaign_type}
              </h4>
            </div>
          </div>
          <span
            className={cn(
              "text-[9px] uppercase font-black tracking-wider px-2 py-0.5 rounded-md shrink-0",
              isCritical
                ? "bg-rose-50 text-rose-700 border border-rose-200"
                : "bg-blue-50 text-blue-700 border border-blue-200",
            )}
          >
            {isCritical ? t("Alta Prioridade") : t("Recomendado")}
          </span>
        </div>

        {/* Mensagem Principal */}
        <p className="text-xs text-gray-700 italic border-l-2 border-blue-300 pl-2.5 py-0.5 leading-relaxed bg-blue-50/20 rounded-r-md">
          "{camp.main_message}"
        </p>

        {/* Métricas Esperadas */}
        {camp.metrics_expected && camp.metrics_expected.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1.5 border-t border-gray-100">
            {camp.metrics_expected.map((metric, i) => (
              <div
                key={i}
                className="flex items-center gap-1 bg-emerald-50/80 px-2 py-0.5 rounded-md border border-emerald-200/80"
              >
                <Target className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                <span className="text-[10px] font-bold text-emerald-700 uppercase">
                  {metric.metric_name}:{" "}
                  <span className="text-emerald-900 font-extrabold">
                    {metric.expected_value}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {camp.cta && (
        <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-[10px]">
          <span className="font-semibold text-gray-400 uppercase tracking-wider">
            {t("CTA:")}
          </span>
          <span className="font-bold text-blue-700 uppercase tracking-wider bg-blue-50 px-2 py-0.5 rounded-sm border border-blue-100">
            {camp.cta}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Painel de Refinamento (Resultados Reais) ─────────────────────────────────
const RefinementPanel: React.FC<{
  refinement: RefinementData;
  onApplyBudget: (alloc: BudgetAllocation[]) => void;
  onClose: () => void;
}> = ({ refinement, onApplyBudget, onClose }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-3.5 shadow-xs space-y-3">
      <div className="flex items-center justify-between gap-2 border-b border-amber-200/60 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <TrendingUp className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-xs sm:text-sm font-bold text-amber-950 uppercase tracking-tight leading-none">
              {t("Análise de Refinamento — Resultados Reais")}
            </h3>
            <span className="text-[10px] text-amber-700 font-medium leading-none">
              {t(
                "Alex analisou os retornos recentes e identificou otimizações",
              )}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOpen(!open)}
            className="p-1 text-amber-700 hover:bg-amber-100 rounded-md transition-colors"
            aria-label={open ? t("Recolher") : t("Expandir")}
          >
            {open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-amber-400 hover:text-amber-800 hover:bg-amber-100 rounded-md transition-colors"
            aria-label={t("Fechar")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {open && (
        <div className="space-y-3">
          {refinement.performance_summary && (
            <p className="text-xs text-amber-900 font-medium italic border-l-2 border-amber-400 pl-2.5 py-1 bg-amber-100/30 rounded-r-md">
              "{refinement.performance_summary}"
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {refinement.what_is_working?.length > 0 && (
              <div className="bg-white rounded-xl p-3 border border-emerald-200/80 shadow-2xs">
                <h4 className="text-[10px] font-black uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  {t("O que está funcionando")}
                </h4>
                <ul className="space-y-1.5">
                  {refinement.what_is_working.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-xs text-gray-700"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1 shrink-0" />
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {refinement.what_is_not_working?.length > 0 && (
              <div className="bg-white rounded-xl p-3 border border-rose-200/80 shadow-2xs">
                <h4 className="text-[10px] font-black uppercase tracking-wider text-rose-700 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                  {t("O que não está convertendo")}
                </h4>
                <ul className="space-y-1.5">
                  {refinement.what_is_not_working.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-xs text-gray-700"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1 shrink-0" />
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {refinement.recommended_changes?.length > 0 && (
            <div className="bg-white rounded-xl p-3 border border-amber-200 shadow-2xs space-y-2">
              <h4 className="text-[10px] font-black uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                <Edit3 className="w-3.5 h-3.5 text-amber-600" />
                {t("Mudanças Recomendadas")}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {refinement.recommended_changes.map((change, i) => (
                  <div
                    key={i}
                    className="border-l-2 border-amber-400 pl-2.5 py-1 space-y-0.5 bg-amber-50/20 rounded-r-md"
                  >
                    <div className="text-[9px] font-black uppercase tracking-wider text-amber-700">
                      {change.area}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5 text-xs min-w-0">
                      <span
                        className="text-gray-400 line-through text-[11px] wrap-break-word line-clamp-2"
                        title={change.current}
                      >
                        {change.current}
                      </span>
                      <span
                        className="text-amber-800 font-bold wrap-break-word line-clamp-2"
                        title={change.recommended}
                      >
                        → {change.recommended}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 italic leading-snug wrap-break-word">
                      {change.reason}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {refinement.updated_budget_allocation?.length > 0 && (
            <div className="flex items-center justify-between pt-1 border-t border-amber-200/50">
              <p className="text-xs text-amber-800 font-medium">
                {t("Nova alocação calculada com base nas métricas reais.")}
              </p>
              <Button
                variant="primary"
                size="sm"
                icon={<Zap className="w-3.5 h-3.5" />}
                onClick={() =>
                  onApplyBudget(refinement.updated_budget_allocation)
                }
                className="bg-amber-600! hover:bg-amber-700! text-xs font-bold"
              >
                {t("Aplicar Nova Alocação")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Componente de Alocação de Verba Editável ─────────────────────────────────
interface EditableBudgetProps {
  items: { channel: string; pct: number }[];
  weeklyBudget: number;
  onChange: (items: { channel: string; pct: number }[]) => void;
  onBudgetChange: (v: number) => void;
}

const EditableBudgetAllocation: React.FC<EditableBudgetProps> = ({
  items,
  weeklyBudget,
  onChange,
  onBudgetChange,
}) => {
  const { t } = useTranslation();
  const total = items.reduce((s, i) => s + (Number(i.pct) || 0), 0);
  const isOver = total > 100;
  const hasBudget = weeklyBudget > 0;

  const updatePct = (idx: number, val: number) => {
    onChange(
      items.map((item, i) =>
        i === idx ? { ...item, pct: Math.max(0, Math.min(100, val)) } : item,
      ),
    );
  };

  const removeItem = (idx: number) =>
    onChange(items.filter((_, i) => i !== idx));

  const addItem = () =>
    onChange([...items, { channel: t("Novo Canal"), pct: 0 }]);

  return (
    <div className="space-y-2.5">
      {/* Verba Semanal Input */}
      <div className="flex items-center justify-between gap-2 bg-emerald-50/50 rounded-xl px-3 py-2 border border-emerald-200">
        <div className="flex flex-col flex-1 min-w-0">
          <label className="text-[9px] font-black uppercase tracking-wider text-emerald-800">
            {t("Verba Semanal Total")}
          </label>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-xs font-bold text-gray-500">R$</span>
            <input
              type="number"
              min={0}
              step={100}
              value={weeklyBudget || ""}
              onChange={(e) => onBudgetChange(Number(e.target.value))}
              placeholder="0"
              className="w-full text-xs sm:text-sm font-black text-emerald-800 bg-transparent border-0 border-b border-emerald-300 focus:outline-hidden focus:border-emerald-600 min-w-0 py-0.5 tabular-nums"
            />
            <span className="text-[10px] text-gray-400 font-medium shrink-0">
              {t("/sem")}
            </span>
          </div>
        </div>
        {hasBudget && (
          <div className="text-right shrink-0 pl-2 border-l border-emerald-200">
            <div className="text-[9px] text-gray-400 uppercase tracking-wider">
              {t("Mensal est.")}
            </div>
            <div className="text-xs font-black text-emerald-800 tabular-nums">
              {fmtCurrency(weeklyBudget * 4.3)}
            </div>
          </div>
        )}
      </div>

      {/* Lista de Canais */}
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {items.map((item, i) => {
          const channelValue = hasBudget
            ? (weeklyBudget * (item.pct || 0)) / 100
            : null;
          return (
            <div
              key={i}
              className="p-2 rounded-lg border border-emerald-100/80 bg-white shadow-2xs space-y-1"
            >
              <div className="flex justify-between items-center gap-2">
                <input
                  type="text"
                  value={item.channel}
                  onChange={(e) =>
                    onChange(
                      items.map((it, idx) =>
                        idx === i ? { ...it, channel: e.target.value } : it,
                      ),
                    )
                  }
                  className="text-xs font-bold text-gray-800 border-0 border-b border-transparent hover:border-emerald-200 focus:border-emerald-500 bg-transparent focus:outline-hidden flex-1 min-w-0 py-0.5"
                />
                <div className="flex items-center gap-1.5 shrink-0">
                  {channelValue !== null && (
                    <span className="text-[10px] font-bold text-gray-500 tabular-nums">
                      {fmtCurrency(channelValue)}
                    </span>
                  )}
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={item.pct}
                    onChange={(e) => updatePct(i, Number(e.target.value))}
                    className="w-11 text-xs font-black text-emerald-700 text-right border border-emerald-200 rounded-sm px-1 py-0.5 focus:outline-hidden focus:ring-1 focus:ring-emerald-400 tabular-nums"
                  />
                  <span className="text-xs text-gray-400">%</span>
                  <button
                    onClick={() => removeItem(i)}
                    className="text-rose-400 hover:text-rose-600 p-0.5 rounded-sm transition-colors"
                    title={t("Remover canal")}
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="h-1 bg-emerald-50 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-300",
                    isOver
                      ? "bg-rose-500"
                      : "bg-linear-to-r from-emerald-500 to-teal-500",
                  )}
                  style={{ width: `${Math.min(item.pct || 0, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer de Ação de Canais */}
      <div className="flex items-center justify-between pt-1 border-t border-emerald-100">
        <button
          onClick={addItem}
          className="flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 font-bold transition-colors"
        >
          <Plus className="w-3 h-3" /> {t("Adicionar Canal")}
        </button>
        <span
          className={cn(
            "text-[11px] font-black tabular-nums",
            isOver ? "text-rose-600" : "text-gray-400",
          )}
        >
          {t("Total:")} {total}% {isOver && t("(excede 100%)")}
        </span>
      </div>
    </div>
  );
};

// ─── Componente de Próximos Passos (Missões Táticas com Gamificação) ──────────
interface EditableNextStepsProps {
  steps: string[];
  isEditing: boolean;
  onChange: (steps: string[]) => void;
  completedQuests?: string[];
  toggleQuest?: (id: string) => void;
}

const EditableNextSteps: React.FC<EditableNextStepsProps> = ({
  steps,
  isEditing,
  onChange,
  completedQuests = [],
  toggleQuest,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 mt-2">
      {steps.slice(0, isEditing ? undefined : 8).map((step, i) => {
        const questId = `step-${i}`;
        const isCompleted = completedQuests.includes(questId);
        return (
          <div
            key={i}
            className={cn(
              "relative flex items-start gap-2.5 p-2.5 rounded-xl border transition-all duration-200",
              isCompleted
                ? "bg-emerald-50/40 border-emerald-200 text-gray-500"
                : "bg-white border-gray-100 hover:border-emerald-200 shadow-2xs",
            )}
          >
            {isEditing ? (
              <div className="w-5 h-5 rounded-md bg-emerald-100 border border-emerald-300 shrink-0 flex items-center justify-center mt-0.5">
                <span className="text-[10px] font-black text-emerald-700">
                  {i + 1}
                </span>
              </div>
            ) : (
              <button
                onClick={() => toggleQuest && toggleQuest(questId)}
                className={cn(
                  "mt-0.5 shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-all",
                  isCompleted
                    ? "bg-emerald-500 border-emerald-500 text-white shadow-2xs"
                    : "bg-white border-gray-300 text-transparent hover:border-emerald-400 hover:bg-emerald-50",
                )}
                aria-label={
                  isCompleted
                    ? t("Marcar como não concluída")
                    : t("Concluir missão")
                }
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            )}

            {isEditing ? (
              <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                <textarea
                  value={step}
                  rows={2}
                  onChange={(e) =>
                    onChange(
                      steps.map((s, idx) => (idx === i ? e.target.value : s)),
                    )
                  }
                  className="w-full text-xs font-medium text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-2 focus:outline-hidden focus:ring-1 focus:ring-emerald-500 resize-none"
                />
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                    {t("Missão")} {i + 1}
                  </span>
                  <button
                    onClick={() =>
                      onChange(steps.filter((_, idx) => idx !== i))
                    }
                    className="text-rose-500 hover:text-rose-700 flex items-center gap-1 text-[10px] font-bold bg-rose-50 px-1.5 py-0.5 rounded-sm transition-colors"
                  >
                    <Minus className="w-2.5 h-2.5" /> {t("Remover")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span
                    className={cn(
                      "text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm",
                      isCompleted
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-indigo-50 text-indigo-700 border border-indigo-100",
                    )}
                  >
                    {isCompleted ? t("Concluída") : `${t("Missão")} ${i + 1}`}
                  </span>
                  <span className="text-[9px] font-bold text-amber-600 flex items-center gap-0.5 bg-amber-50 px-1 py-0.5 rounded-sm border border-amber-100">
                    <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />{" "}
                    +50 XP
                  </span>
                </div>
                <span
                  className={cn(
                    "text-xs font-semibold leading-relaxed block wrap-break-word",
                    isCompleted
                      ? "line-through text-gray-400"
                      : "text-gray-800",
                  )}
                >
                  {step}
                </span>
              </div>
            )}
          </div>
        );
      })}

      {isEditing && (
        <button
          onClick={() => onChange([...steps, ""])}
          className="w-full py-2 flex items-center justify-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 border-dashed rounded-lg font-bold transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> {t("Adicionar Nova Missão")}
        </button>
      )}
    </div>
  );
};

// ─── Componente de Chatbox do Agente ──────────────────────────────────────────
const AgentCommandInput: React.FC<{
  agentId: "mia" | "max" | "sarah" | "alex";
  agentFirstName: string;
  sessionId?: number;
  modelName?: string;
  placeholder?: string;
  onUpdates: (updates: Partial<StrategyData>) => void;
}> = ({
  agentId,
  agentFirstName,
  sessionId,
  modelName,
  placeholder,
  onUpdates,
}) => {
  const { t } = useTranslation();
  const [cmd, setCmd] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const trimmed = cmd.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const res = await adsStrategyService.sendAgentCommand(
        agentId,
        trimmed,
        sessionId,
        modelName,
      );
      if (res.success && res.updates) {
        onUpdates(res.updates);
        setCmd("");
        showToastSuccess(
          t("{agent} atualizou a estratégia conforme solicitado!", {
            agent: agentFirstName,
          }),
        );
      }
    } catch {
      showToastError(
        t("Erro ao enviar comando para {agent}", { agent: agentFirstName }),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative mt-2">
      <input
        type="text"
        value={cmd}
        onChange={(e) => setCmd(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSend()}
        placeholder={
          placeholder || `${t("Dar ordens para")} ${agentFirstName}...`
        }
        disabled={sending}
        className="w-full text-xs bg-white border border-purple-100 rounded-lg pl-2.5 pr-8 py-1.5 focus:outline-hidden focus:border-purple-400 focus:ring-1 focus:ring-purple-300 disabled:opacity-50 text-gray-800 placeholder-gray-400 shadow-2xs"
      />
      <button
        onClick={handleSend}
        disabled={!cmd.trim() || sending}
        className="absolute right-1.5 top-1 p-1 text-purple-600 hover:text-purple-800 disabled:opacity-30 disabled:hover:text-purple-600 transition-colors"
        title={t("Enviar instrução")}
      >
        {sending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Send className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
};

// ─── Página Principal: Estratégia de Marketing ────────────────────────────────
const CampaignStrategy: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<StrategyData | null>(null);
  const [organicData, setOrganicData] = useState<OrganicStrategyData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [isResearching, setIsResearching] = useState(false);
  const [isSuggestingOrganic, setIsSuggestingOrganic] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [selectedModel, setSelectedModel] = useState<string>("gpt-5.4-nano");
  const [objective, setObjective] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);

  // Estados locais editáveis
  const [editBudget, setEditBudget] = useState<
    { channel: string; pct: number }[]
  >([]);
  const [editSteps, setEditSteps] = useState<string[]>([]);
  const [editSummary, setEditSummary] = useState<string>("");
  const [weeklyBudget, setWeeklyBudget] = useState<number>(0);

  // Auto-save timer
  const budgetAutoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hasLoadedOnce = useRef(false);

  // Modo Avançado (JSON) & Anotações
  const [advancedMode, setAdvancedMode] = useState(false);
  const [advancedJsonStr, setAdvancedJsonStr] = useState("");
  const [userNotes, setUserNotes] = useState("");

  // Gamificação (Quests & Readiness)
  const [completedQuests, setCompletedQuests] = useState<string[]>([]);

  const toggleQuest = (id: string) => {
    setCompletedQuests((prev) => {
      const isCompleted = prev.includes(id);
      if (!isCompleted) {
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
      }
      const next = isCompleted ? prev.filter((q) => q !== id) : [...prev, id];
      if (data?.session_id) {
        adsStrategyService
          .saveStrategy(
            data.session_id,
            { completed_quests: next } as Partial<StrategyData>,
            objective || undefined,
          )
          .catch(() => {});
      }
      return next;
    });
  };

  const totalQuests =
    editSteps.length + (organicData?.suggestions?.length || 0);
  const readinessScore =
    totalQuests > 0
      ? Math.round((completedQuests.length / totalQuests) * 100)
      : 0;

  const availableModels = [
    {
      id: "gpt-5.4-nano",
      name: t("GPT-5.4 Nano"),
      badge: t("Rápido"),
    },
    {
      id: "gpt-5.4-mini",
      name: t("GPT-5.4 Mini"),
      badge: t("Equilíbrio"),
    },
    {
      id: "claude-3.5-sonnet",
      name: t("Claude 3.5"),
      badge: t("Deep"),
    },
    {
      id: "gpt-5.4",
      name: t("GPT-5.4 Pro"),
      badge: t("Flagship"),
    },
  ];

  const parseBudgetItems = (d: StrategyData) =>
    Array.isArray(d.suggested_budget_allocation)
      ? (d.suggested_budget_allocation as BudgetAllocation[]).map((b) => ({
          channel: b.channel || t("Canal"),
          pct: b.allocation_percentage || 0,
        }))
      : Object.entries(d.suggested_budget_allocation || {}).map(([k, v]) => ({
          channel: k,
          pct: Number(v) || 0,
        }));

  const syncEdits = (d: StrategyData) => {
    setEditBudget(parseBudgetItems(d));
    setEditSteps(d.next_steps || []);
    setEditSummary(d.overall_strategy_summary || "");
    setUserNotes(d.user_notes || "");
    setWeeklyBudget(d.weekly_budget || 0);
    if (Array.isArray((d as any).completed_quests)) {
      setCompletedQuests((d as any).completed_quests);
    }
    const { session_id, has_user_edits, is_approved, ...clone } = d;
    setAdvancedJsonStr(JSON.stringify(clone, null, 2));
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const result = await adsStrategyService.getStrategy();
        if (!isMounted) return;
        if (result && result.recommended_campaigns) {
          setData(result);
          syncEdits(result);
          if (result.campaign_objective)
            setObjective(result.campaign_objective);
          if (result.organic_suggestions) {
            setOrganicData({ suggestions: result.organic_suggestions });
          }
        }
      } catch (err) {
        console.warn("Sem estratégia anterior:", err);
      } finally {
        if (isMounted) {
          setFetching(false);
          setTimeout(() => {
            hasLoadedOnce.current = true;
          }, 300);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  // Auto-save do orçamento semanal com debounce de 1.5s
  useEffect(() => {
    if (!hasLoadedOnce.current || !data?.session_id) return;
    if (budgetAutoSaveTimer.current) clearTimeout(budgetAutoSaveTimer.current);
    budgetAutoSaveTimer.current = setTimeout(async () => {
      try {
        const userEdits: Partial<StrategyData> = {
          suggested_budget_allocation: editBudget.map((i) => ({
            channel: i.channel,
            allocation_percentage: i.pct,
          })),
          weekly_budget: weeklyBudget,
        };
        await adsStrategyService.saveStrategy(
          data.session_id,
          userEdits,
          objective || undefined,
        );
      } catch {
        // Auto-save silencioso em background
      }
    }, 1500);
    return () => {
      if (budgetAutoSaveTimer.current)
        clearTimeout(budgetAutoSaveTimer.current);
    };
  }, [weeklyBudget, editBudget, data?.session_id, objective]);

  const applyNewData = (result: StrategyData) => {
    setData(result);
    syncEdits(result);
    if (result.campaign_objective) setObjective(result.campaign_objective);
    setIsEditing(false);
  };

  const handleRecommend = async () => {
    setLoading(true);
    try {
      applyNewData(
        await adsStrategyService.recommendStrategy(selectedModel, objective),
      );
      showToastSuccess(t("Comitê de Agentes concluiu a análise com sucesso!"));
    } catch (err: any) {
      showToastError(
        err.response?.status === 402
          ? err.response?.data?.error || t("Créditos insuficientes.")
          : t("Erro ao comunicar com os Agentes."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeepResearch = async () => {
    setIsResearching(true);
    try {
      applyNewData(
        await adsStrategyService.deepResearchStrategy(selectedModel, objective),
      );
      showToastSuccess(
        t("Deep Research concluída com novos insights de mercado!"),
      );
    } catch (err: any) {
      showToastError(
        err.response?.status === 402
          ? err.response?.data?.error || t("Créditos insuficientes.")
          : t("Erro na Deep Research."),
      );
    } finally {
      setIsResearching(false);
    }
  };

  const handleSuggestOrganic = async () => {
    setIsSuggestingOrganic(true);
    try {
      const suggestions = await adsStrategyService.suggestOrganicPosts(
        selectedModel,
        data?.session_id,
      );
      setOrganicData(suggestions);
      showToastSuccess(
        t("Mia gerou 3 sugestões orgânicas alinhadas à estratégia!"),
      );
    } catch (err: any) {
      showToastError(
        err.response?.status === 402
          ? err.response?.data?.error || t("Créditos insuficientes.")
          : t("Erro ao sugerir posts orgânicos."),
      );
    } finally {
      setIsSuggestingOrganic(false);
    }
  };

  const handleFeedback = async (isApproved: boolean) => {
    try {
      await adsStrategyService.sendFeedback(isApproved);
      showToastSuccess(
        isApproved
          ? t("Estratégia aprovada e confirmada!")
          : t("Feedback registrado. O comitê reavaliará as premissas."),
      );
      setData((prev) => (prev ? { ...prev, is_approved: isApproved } : prev));
    } catch {
      showToastError(t("Erro ao enviar feedback."));
    }
  };

  const handleSaveEdits = async () => {
    if (!data) return;
    setIsSaving(true);
    try {
      if (advancedMode) {
        try {
          const parsedData = JSON.parse(advancedJsonStr);
          parsedData.user_notes = userNotes;
          const res = await adsStrategyService.saveStrategyEdits(parsedData);
          setData(res.data);
          syncEdits(res.data);
          setIsEditing(false);
          setAdvancedMode(false);
          showToastSuccess(t("Estratégia avançada salva com sucesso!"));
        } catch {
          showToastError(
            t("JSON inválido! Verifique a sintaxe antes de salvar."),
          );
          return;
        }
      } else {
        const userEdits: Partial<StrategyData> = {
          overall_strategy_summary: editSummary,
          next_steps: editSteps,
          suggested_budget_allocation: editBudget.map((i) => ({
            channel: i.channel,
            allocation_percentage: i.pct,
          })),
          user_notes: userNotes,
          organic_suggestions: organicData?.suggestions,
          weekly_budget: weeklyBudget,
        };
        const res = await adsStrategyService.saveStrategy(
          data.session_id,
          userEdits,
          objective,
        );
        setData(res.strategy);
        setIsEditing(false);
        showToastSuccess(t("Estratégia salva com sucesso!"));
      }
    } catch {
      showToastError(t("Erro ao salvar edições."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (data) syncEdits(data);
    setIsEditing(false);
  };

  const handleRefine = async () => {
    if (!data) return;
    setIsRefining(true);
    try {
      const res = await adsStrategyService.refineStrategy(
        data.session_id,
        selectedModel,
      );
      setData((prev) =>
        prev ? { ...prev, refinement: res.refinement } : prev,
      );
      showToastSuccess(t("Alex analisou os resultados e gerou recomendações!"));
    } catch (err: any) {
      showToastError(
        err.response?.status === 402
          ? err.response?.data?.error || t("Créditos insuficientes.")
          : t("Erro ao refinar estratégia."),
      );
    } finally {
      setIsRefining(false);
    }
  };

  const handleApplyRefinedBudget = (alloc: BudgetAllocation[]) => {
    const newItems = alloc.map((b) => ({
      channel: b.channel,
      pct: b.allocation_percentage,
    }));
    setEditBudget(newItems);
    setIsEditing(true);
    showToastSuccess(
      t("Nova alocação aplicada. Clique em Salvar para confirmar."),
    );
  };

  const handleDismissRefinement = () => {
    setData((prev) => (prev ? { ...prev, refinement: null } : prev));
  };

  const handleApplyStrategy = async () => {
    if (!data?.session_id) return;
    setIsApplying(true);
    try {
      await adsStrategyService.applyStrategy(data.session_id);
      showToastSuccess(
        t(
          "Estratégia enviada para execução! Processamento em background iniciado.",
        ),
      );
    } catch (err: any) {
      showToastError(
        err.response?.data?.error ||
          t("Erro ao aplicar estratégia nas contas."),
      );
    } finally {
      setIsApplying(false);
    }
  };

  const anyBusy =
    loading ||
    isResearching ||
    isSuggestingOrganic ||
    isRefining ||
    isSaving ||
    isApplying;

  return (
    <PageLayout
      title={t("Estratégia de Marketing")}
      subtitle={t(
        "Comitê de IA: inteligência tática, alocação de verba e geração de campanhas multicanal.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={fetching}
      skeleton={<CampaignStrategySkeleton />}
      actions={
        <div className="flex items-center gap-1.5 flex-nowrap">
          {/* Seletor de Modelo */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg px-2 py-1 shadow-2xs shrink-0">
            <Bot className="h-3.5 w-3.5 text-purple-600 mr-1.5 shrink-0" />
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={anyBusy}
              className="bg-transparent border-0 text-xs font-bold text-gray-800 focus:ring-0 outline-hidden cursor-pointer pr-1 w-[100px] sm:w-auto"
            >
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.badge})
                </option>
              ))}
            </select>
          </div>

          {/* Feedback Rápido (Aprovar / Refazer) */}
          {data && !isEditing && (
            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5 shadow-2xs shrink-0">
              <button
                onClick={() => handleFeedback(true)}
                disabled={anyBusy}
                className={cn(
                  "px-2 py-1 rounded-sm text-xs font-bold flex items-center gap-1 transition-all",
                  data.is_approved === true
                    ? "bg-emerald-500 text-white shadow-2xs"
                    : "text-gray-500 hover:text-emerald-700 hover:bg-emerald-50",
                )}
                title={t("Aprovar Estratégia")}
              >
                <ThumbsUp className="h-3 w-3 shrink-0" />
                <span className="hidden xl:inline">{t("Aprovar")}</span>
              </button>
              <button
                onClick={() => handleFeedback(false)}
                disabled={anyBusy}
                className={cn(
                  "px-2 py-1 rounded-sm text-xs font-bold flex items-center gap-1 transition-all",
                  data.is_approved === false
                    ? "bg-rose-500 text-white shadow-2xs"
                    : "text-gray-500 hover:text-rose-700 hover:bg-rose-50",
                )}
                title={t("Refazer Estratégia")}
              >
                <ThumbsDown className="h-3 w-3 shrink-0" />
                <span className="hidden xl:inline">{t("Refazer")}</span>
              </button>
            </div>
          )}

          {/* Botão Ideias Orgânicas (Mia) */}
          <Button
            variant="outline"
            size="sm"
            icon={
              isSuggestingOrganic ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin shrink-0" />
              ) : (
                <Lightbulb className={HEADER_SECONDARY_ICON} />
              )
            }
            onClick={handleSuggestOrganic}
            disabled={anyBusy}
            className={`${HEADER_SECONDARY_BUTTON} whitespace-nowrap shrink-0`}
            title={t("Ideias Orgânicas")}
          >
            <span className="hidden xl:inline text-xs">
              {t("Ideias Orgânicas")}
            </span>
          </Button>

          {/* Botão Deep Research */}
          <Button
            variant="outline"
            size="sm"
            icon={
              isResearching ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin shrink-0" />
              ) : (
                <Globe className={HEADER_SECONDARY_ICON} />
              )
            }
            onClick={handleDeepResearch}
            disabled={anyBusy}
            className={`${HEADER_SECONDARY_BUTTON} whitespace-nowrap shrink-0`}
            title={t("Deep Research")}
          >
            <span className="hidden xl:inline text-xs">
              {t("Deep Research")}
            </span>
          </Button>

          {/* Botão Principal: Gerar / Atualizar Estratégia */}
          <Button
            variant="primary"
            size="sm"
            icon={
              loading ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )
            }
            onClick={handleRecommend}
            disabled={anyBusy}
            className={`${HEADER_PRIMARY_BUTTON} whitespace-nowrap`}
          >
            <span className="text-xs">
              {data ? t("Atualizar Estratégia") : t("Gerar Estratégia")}
            </span>
          </Button>
        </div>
      }
    >
      <div className="w-full flex flex-col gap-2">
        {/* ========================================================================= */}
        {/* BANDA 1: Controle Estratégico & Indicador de Prontidão (Grid 8/4)          */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-stretch">
          {/* Card 1: Objetivo Estratégico da Campanha */}
          <div className="lg:col-span-7 xl:col-span-8 bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between">
            <div>
              <SectionHeader
                icon={<Target className="h-4 w-4" />}
                title={t("Objetivo Estratégico")}
                subtitle={t("Diretriz Principal para o Comitê de Agentes")}
                iconBgClass="bg-purple-600"
                action={
                  <div className="flex items-center gap-1.5 self-center">
                    {data?.campaign_objective ? (
                      <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-md">
                        <CheckCircle2 className="h-3 w-3 text-purple-600" />
                        {t("Objetivo Ativo")}
                      </span>
                    ) : (
                      <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                        <AlertCircle className="h-3 w-3 text-amber-600" />
                        {t("Defina um objetivo")}
                      </span>
                    )}
                  </div>
                }
              />

              <div className="relative">
                <textarea
                  id="objective"
                  rows={2}
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder={t(
                    "Ex: Gerar Leads B2B qualificados para nosso serviço de consultoria, focando em diretores de marketing e expansão de receita...",
                  )}
                  className="w-full text-xs font-medium text-gray-800 rounded-lg border border-purple-200 bg-purple-50/20 p-2.5 focus:outline-hidden focus:border-purple-500 focus:ring-1 focus:ring-purple-300 resize-none transition-colors leading-relaxed"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 mt-1.5 border-t border-purple-50 text-[11px] text-gray-400">
              <span className="flex items-center gap-1 truncate">
                <Info className="h-3 w-3 text-purple-500 shrink-0" />
                {t("Clique em")} <strong>"{t("Atualizar Estratégia")}"</strong>{" "}
                {t("para reavaliar as diretrizes.")}
              </span>
              <span className="text-[10px] font-semibold text-gray-400 tabular-nums shrink-0 ml-2">
                {objective.length}/500
              </span>
            </div>
          </div>

          {/* Card 2: Prontidão & Gamificação da Campanha */}
          <div className="lg:col-span-5 xl:col-span-4 bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between">
            <div>
              <SectionHeader
                icon={<Activity className="h-4 w-4" />}
                title={t("Prontidão da Campanha")}
                subtitle={t("Execução e Missões Táticas")}
                iconBgClass="bg-indigo-600"
                action={
                  <span
                    className={cn(
                      "text-sm font-black tabular-nums px-2 py-0.5 rounded-md border",
                      readinessScore === 100
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-purple-50 text-purple-700 border-purple-200",
                    )}
                  >
                    {readinessScore}%
                  </span>
                }
              />

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 font-medium">
                    {completedQuests.length} {t("de")} {totalQuests}{" "}
                    {t("missões concluídas")}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                    {readinessScore === 100
                      ? t("Pronta para rodar")
                      : t("Em estruturação")}
                  </span>
                </div>

                {/* Barra de Progresso com Transição Suave */}
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden shadow-inner">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all duration-700 ease-out",
                      readinessScore === 100
                        ? "bg-emerald-500"
                        : "bg-linear-to-r from-purple-500 to-indigo-600",
                    )}
                    style={{ width: `${readinessScore}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Ações Táticas Rápidas */}
            <div className="flex items-center gap-1.5 pt-2 mt-2 border-t border-purple-50 flex-wrap">
              {data && !isEditing && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Edit3 className="h-3 w-3" />}
                    onClick={() => setIsEditing(true)}
                    className="flex-1 h-7! py-0.5! px-2! rounded-lg text-xs font-semibold border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    {t("Editar")}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    icon={
                      isRefining ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <TrendingUp className="h-3 w-3 text-amber-600" />
                      )
                    }
                    onClick={handleRefine}
                    disabled={anyBusy}
                    className="flex-1 h-7! py-0.5! px-2! rounded-lg text-xs font-semibold border-amber-200! text-amber-700! bg-amber-50! hover:bg-amber-100!"
                  >
                    {t("Refinar")}
                  </Button>

                  <Button
                    variant="primary"
                    size="sm"
                    icon={
                      isApplying ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Zap className="h-3 w-3" />
                      )
                    }
                    onClick={handleApplyStrategy}
                    disabled={anyBusy}
                    className="flex-1 h-7! py-0.5! px-2! rounded-lg text-xs font-bold whitespace-nowrap bg-indigo-600! hover:bg-indigo-700!"
                  >
                    {t("Executar")}
                  </Button>
                </>
              )}

              {isEditing && (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={
                      isSaving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )
                    }
                    onClick={handleSaveEdits}
                    disabled={isSaving}
                    className="flex-1 h-7! py-0.5! px-2! rounded-lg text-xs font-bold bg-emerald-600! hover:bg-emerald-700!"
                  >
                    {t("Salvar")}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<X className="h-3 w-3" />}
                    onClick={handleCancelEdit}
                    className="flex-1 h-7! py-0.5! px-2! rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100"
                  >
                    {t("Cancelar")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BANDA CONDICIONAL: Painel de Refinamento & Banner de Edição               */}
        {/* ========================================================================= */}
        {data?.refinement && (
          <RefinementPanel
            refinement={data.refinement}
            onApplyBudget={handleApplyRefinedBudget}
            onClose={handleDismissRefinement}
          />
        )}

        {isEditing && (
          <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 shadow-xs space-y-2.5">
            <div className="flex items-center justify-between gap-2 border-b border-emerald-200/60 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <Edit3 className="w-3.5 h-3.5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-emerald-950 uppercase tracking-tight leading-none">
                    {t("Modo de Edição Ativo")}
                  </h4>
                  <span className="text-[10px] text-emerald-700 font-medium leading-none">
                    {t(
                      "Ajuste os dados diretamente nos cards ou acesse o JSON bruto.",
                    )}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setAdvancedMode(!advancedMode)}
                className={cn(
                  "text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md transition-colors border",
                  advancedMode
                    ? "bg-emerald-600 text-white border-emerald-700"
                    : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-100",
                )}
              >
                {advancedMode ? t("Modo Padrão") : t("Modo Avançado (JSON)")}
              </button>
            </div>

            <div className="space-y-2">
              <div>
                <label className="block text-[10px] font-bold text-emerald-900 uppercase tracking-wider mb-1">
                  {t("Anotações do Estrategista (Persistidas na sessão):")}
                </label>
                <textarea
                  value={userNotes}
                  onChange={(e) => setUserNotes(e.target.value)}
                  placeholder={t(
                    "Ex: Focar 80% do tráfego pago em LinkedIn Ads e 20% em Meta Ads no início...",
                  )}
                  rows={2}
                  className="w-full text-xs font-medium rounded-lg border border-emerald-200 bg-white p-2 focus:outline-hidden focus:border-emerald-500 focus:ring-1 focus:ring-emerald-300"
                />
              </div>

              {advancedMode && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-bold text-emerald-900 uppercase tracking-wider">
                      {t("Editor JSON Estrutural:")}
                    </label>
                    <button
                      onClick={() => {
                        if (!data) return;
                        const {
                          session_id,
                          has_user_edits,
                          is_approved,
                          ...clone
                        } = data;
                        setAdvancedJsonStr(JSON.stringify(clone, null, 2));
                      }}
                      className="text-[9px] uppercase font-bold text-emerald-700 hover:text-emerald-900"
                    >
                      {t("Restaurar Original")}
                    </button>
                  </div>
                  <textarea
                    value={advancedJsonStr}
                    onChange={(e) => setAdvancedJsonStr(e.target.value)}
                    className="w-full h-48 text-xs font-mono bg-gray-900 text-emerald-400 p-3 rounded-lg border border-gray-800 focus:ring-1 focus:ring-emerald-500 shadow-inner"
                    spellCheck={false}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ESTADO VAZIO (Antes da primeira geração)                                  */}
        {/* ========================================================================= */}
        {!data && !loading && !isResearching && !isSuggestingOrganic && (
          <Card
            variant="glass"
            className="w-full p-8 rounded-xl border border-dashed border-purple-200 flex flex-col items-center justify-center text-center bg-purple-50/20"
          >
            <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 mb-3 shadow-2xs">
              <Bot className="h-7 w-7" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">
              {t("O Comitê de Agentes está Aguardando")}
            </h3>
            <p className="text-xs text-gray-500 max-w-md mb-4 leading-relaxed">
              {t(
                "Defina o objetivo principal da sua campanha no campo acima e clique em 'Gerar Estratégia' para que Sarah, Max, Alex e Mia estruturem seu plano de marketing.",
              )}
            </p>
            <Button
              variant="primary"
              size="md"
              icon={<Sparkles className="h-4 w-4" />}
              onClick={handleRecommend}
              className="rounded-lg shadow-xs font-bold text-xs"
            >
              {t("Gerar Estratégia com IA")}
            </Button>
          </Card>
        )}

        {/* ========================================================================= */}
        {/* BANDA 2: Comitê de Agentes de IA (Grid 2x2 Equilibrado)                   */}
        {/* ========================================================================= */}
        {(data || organicData) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-stretch w-full">
            {/* ───────────────────────────────────────────────────────────────────── */}
            {/* AGENTE 1: Sarah (CMO) — Estratégia & Visão Geral                      */}
            {/* ───────────────────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between h-full">
              <div>
                <SectionHeader
                  icon={<Sparkles className="h-4 w-4" />}
                  title={AGENT_CONFIGS.strategist.name}
                  subtitle={AGENT_CONFIGS.strategist.role}
                  iconBgClass={AGENT_CONFIGS.strategist.color}
                  action={
                    <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200">
                      {AGENT_CONFIGS.strategist.badge}
                    </span>
                  }
                />

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs text-purple-900 font-bold">
                    <MessageSquare className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                    <span>{t("Tese & Resumo Estratégico:")}</span>
                  </div>

                  {isEditing ? (
                    <textarea
                      rows={6}
                      value={editSummary}
                      onChange={(e) => setEditSummary(e.target.value)}
                      className="w-full text-xs text-gray-800 border border-purple-200 rounded-lg p-2.5 bg-white focus:outline-hidden focus:ring-1 focus:ring-purple-400 resize-none leading-relaxed"
                    />
                  ) : (
                    <div className="p-3 bg-purple-50/30 rounded-xl border border-purple-100/80 text-xs font-medium text-gray-800 leading-relaxed italic">
                      "{editSummary || data?.overall_strategy_summary}"
                    </div>
                  )}
                </div>
              </div>

              {/* Chatbox para o Agente Sarah */}
              <div className="pt-2 border-t border-purple-50">
                <AgentCommandInput
                  agentId={AGENT_CONFIGS.strategist.apiId}
                  agentFirstName="Sarah"
                  sessionId={data?.session_id}
                  modelName={selectedModel}
                  placeholder={`${t("Dar ordens para")} Sarah...`}
                  onUpdates={(updates: Partial<StrategyData>) => {
                    if (!data) return;
                    const merged = { ...data, ...updates };
                    setData(merged);
                    syncEdits(merged);
                  }}
                />
              </div>
            </div>

            {/* ───────────────────────────────────────────────────────────────────── */}
            {/* AGENTE 2: Max (Tráfego) — Canais & Campanhas Ads                      */}
            {/* ───────────────────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between h-full">
              <div>
                <SectionHeader
                  icon={<Target className="h-4 w-4" />}
                  title={AGENT_CONFIGS.traffic.name}
                  subtitle={AGENT_CONFIGS.traffic.role}
                  iconBgClass={AGENT_CONFIGS.traffic.color}
                  action={
                    <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                      {AGENT_CONFIGS.traffic.badge}
                    </span>
                  }
                />

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs text-blue-900 font-bold">
                    <MessageSquare className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                    <span>{t("Campanhas Recomendadas:")}</span>
                  </div>

                  <div className="flex flex-col gap-2">
                    {(data?.recommended_campaigns || [])
                      .slice(0, 2)
                      .map((camp, idx) => (
                        <CampaignCard key={idx} camp={camp} idx={idx} />
                      ))}
                  </div>
                </div>
              </div>

              {/* Chatbox para o Agente Max */}
              <div className="pt-2 border-t border-purple-50">
                <AgentCommandInput
                  agentId={AGENT_CONFIGS.traffic.apiId}
                  agentFirstName="Max"
                  sessionId={data?.session_id}
                  modelName={selectedModel}
                  placeholder={`${t("Dar ordens para")} Max...`}
                  onUpdates={(updates: Partial<StrategyData>) => {
                    if (!data) return;
                    const merged = { ...data, ...updates };
                    setData(merged);
                    syncEdits(merged);
                  }}
                />
              </div>
            </div>

            {/* ───────────────────────────────────────────────────────────────────── */}
            {/* AGENTE 3: Alex (Analytics) — Verba & Missões Táticas                  */}
            {/* ───────────────────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between h-full">
              <div>
                <SectionHeader
                  icon={<BarChart3 className="h-4 w-4" />}
                  title={AGENT_CONFIGS.data.name}
                  subtitle={AGENT_CONFIGS.data.role}
                  iconBgClass={AGENT_CONFIGS.data.color}
                  action={
                    <div className="flex items-center gap-1">
                      {data?.has_user_edits && (
                        <span className="text-[9px] bg-emerald-100 text-emerald-800 font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-200">
                          {t("Editado")}
                        </span>
                      )}
                      <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {AGENT_CONFIGS.data.badge}
                      </span>
                    </div>
                  }
                />

                <div className="space-y-3">
                  {/* Seção 1: Alocação de Verba */}
                  <div className="bg-emerald-50/30 rounded-xl p-2.5 border border-emerald-100 shadow-2xs">
                    <h4 className="text-[10px] font-black uppercase tracking-wider text-emerald-800 mb-2 flex items-center gap-1.5">
                      <Coins className="w-3 h-3 text-emerald-600" />
                      {t("Distribuição de Verba por Canal")}
                    </h4>
                    <EditableBudgetAllocation
                      items={editBudget}
                      weeklyBudget={weeklyBudget}
                      onChange={setEditBudget}
                      onBudgetChange={setWeeklyBudget}
                    />
                  </div>

                  {/* Seção 2: Missões Táticas */}
                  <div className="bg-emerald-50/30 rounded-xl p-2.5 border border-emerald-100 shadow-2xs">
                    <h4 className="text-[10px] font-black uppercase tracking-wider text-emerald-800 mb-1.5 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      {t("Missões Táticas da Campanha")}
                    </h4>
                    <EditableNextSteps
                      steps={editSteps}
                      isEditing={isEditing}
                      onChange={setEditSteps}
                      completedQuests={completedQuests}
                      toggleQuest={toggleQuest}
                    />
                  </div>
                </div>
              </div>

              {/* Chatbox para o Agente Alex */}
              <div className="pt-2 border-t border-purple-50 mt-2">
                <AgentCommandInput
                  agentId={AGENT_CONFIGS.data.apiId}
                  agentFirstName="Alex"
                  sessionId={data?.session_id}
                  modelName={selectedModel}
                  placeholder={`${t("Dar ordens para")} Alex...`}
                  onUpdates={(updates: Partial<StrategyData>) => {
                    if (!data) return;
                    const merged = { ...data, ...updates };
                    setData(merged);
                    syncEdits(merged);
                  }}
                />
              </div>
            </div>

            {/* ───────────────────────────────────────────────────────────────────── */}
            {/* AGENTE 4: Mia (Social Media) — Conteúdo Orgânico                      */}
            {/* ───────────────────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs flex flex-col justify-between h-full">
              <div>
                <SectionHeader
                  icon={<Users className="h-4 w-4" />}
                  title={AGENT_CONFIGS.social.name}
                  subtitle={AGENT_CONFIGS.social.role}
                  iconBgClass={AGENT_CONFIGS.social.color}
                  action={
                    <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-pink-50 text-pink-700 border border-pink-200">
                      {AGENT_CONFIGS.social.badge}
                    </span>
                  }
                />

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs text-pink-900 font-bold">
                    <MessageSquare className="h-3.5 w-3.5 text-pink-600 shrink-0" />
                    <span>{t("Ideias Orgânicas Alinhadas:")}</span>
                  </div>

                  {organicData ? (
                    <div className="flex flex-col gap-2 max-h-[380px] overflow-y-auto pr-1">
                      {(organicData.suggestions || []).map((sugg, idx) => {
                        const questId = `sugg-${idx}`;
                        const isCompleted = completedQuests.includes(questId);
                        return (
                          <div
                            key={idx}
                            className={cn(
                              "bg-white rounded-xl p-3 border shadow-2xs space-y-1.5 transition-all",
                              isCompleted
                                ? "border-pink-300 bg-pink-50/20 opacity-80"
                                : "border-pink-100 hover:border-pink-200",
                            )}
                          >
                            <div className="flex justify-between items-center gap-2">
                              <span className="text-[9px] uppercase tracking-wider text-pink-700 font-black px-1.5 py-0.5 bg-pink-50 rounded-sm border border-pink-100">
                                {sugg.format}
                              </span>
                              <button
                                onClick={() => toggleQuest(questId)}
                                className={cn(
                                  "flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-sm transition-colors border",
                                  isCompleted
                                    ? "bg-pink-600 border-pink-600 text-white"
                                    : "bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100",
                                )}
                              >
                                {isCompleted ? (
                                  <CheckCircle2 className="w-3 h-3" />
                                ) : (
                                  <div className="w-3 h-3 rounded-full border-2 border-pink-400" />
                                )}
                                <span>
                                  {isCompleted ? t("Armado") : t("Armar Post")}
                                </span>
                              </button>
                            </div>

                            <h4 className="text-xs font-bold text-gray-900 leading-tight">
                              {sugg.topic}
                            </h4>

                            <p className="text-[11px] text-gray-700 border-l-2 border-pink-300 pl-2 py-0.5 italic bg-pink-50/20 rounded-r">
                              "{sugg.hook}"
                            </p>

                            <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed">
                              {sugg.copy}
                            </p>

                            {sugg.cta && (
                              <div className="pt-1.5 border-t border-pink-100 flex items-center justify-between text-[9px]">
                                <span className="font-bold text-gray-400 uppercase">
                                  {t("CTA:")}
                                </span>
                                <span className="font-bold text-pink-700 uppercase bg-pink-50 px-1.5 py-0.5 rounded-sm border border-pink-100">
                                  {sugg.cta}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center bg-pink-50/20 rounded-xl border border-dashed border-pink-200">
                      <Users className="h-8 w-8 text-pink-300 mb-2" />
                      <p className="text-xs font-semibold text-gray-700">
                        {t("Nenhuma sugestão orgânica carregada")}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5 max-w-xs mb-3">
                        {t(
                          "Clique no botão abaixo para que Mia analise métricas passadas e gere posts virais.",
                        )}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Lightbulb className="h-3 w-3 text-pink-600" />}
                        onClick={handleSuggestOrganic}
                        disabled={anyBusy}
                        className="rounded-lg h-7! py-0.5! px-2.5! border-pink-200! text-pink-700! bg-pink-50! hover:bg-pink-100! text-xs font-semibold"
                      >
                        {t("Gerar Ideias Orgânicas")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Chatbox para o Agente Mia */}
              <div className="pt-2 border-t border-purple-50 mt-2">
                <AgentCommandInput
                  agentId={AGENT_CONFIGS.social.apiId}
                  agentFirstName="Mia"
                  sessionId={data?.session_id}
                  modelName={selectedModel}
                  placeholder={`${t("Dar ordens para")} Mia...`}
                  onUpdates={(updates: Partial<StrategyData>) => {
                    if (!data) return;
                    const merged = { ...data, ...updates };
                    setData(merged);
                    syncEdits(merged);
                    if (updates.organic_suggestions) {
                      setOrganicData({
                        suggestions: updates.organic_suggestions,
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default CampaignStrategy;
